import React from 'react';
import {
  Alert, Box, Button, Link, List, ListItem, Skeleton, Stack, Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Link as RouterLink } from 'react-router-dom';
import Pill from '../common/Pill';
import { SP } from '../../theme/tokens';

/**
 * BundleComponentList — the one list of a Shopify bundle's components.
 *
 * @description
 *   Renders the component rows of a bundle parent (title · SKU · line cost ·
 *   quantity) together with the list's own loading, error and empty states.
 *   It owns NO fetch: both callers already hold the payload of
 *   `GET /api/products/bundle/<product_id>/components` and pass the rows in.
 *   Exists because the products-list SlidePanel and the bundle detail page
 *   rendered the same rows twice, and the copies had already drifted (language,
 *   cost line, right-hand spacing, link semantics) within one branch.
 *
 *   🔴 The deep link is a real link (`href`), not a button that navigates: it
 *   opens in a new tab on middle-click and announces as a link. It carries NO
 *   "open in new" icon, because it does not open a new tab.
 *   🔴 The quantity sits in the flex row, not in MUI's absolutely positioned
 *   `secondaryAction` — a long title wraps beside it instead of running under
 *   it (measured at 375 px: the old chip overlapped the title's last line).
 *   🔴 The cost helpers below say "Unknown" for an unknown total and name the
 *   fallback as a fallback — never a bare number that knows less than it claims
 *   (Rule 22 §3). The word "approximate" stood here until 2026-10-09 and is gone
 *   with A1.2: the display sums the SAME cost term the automation uses, so there
 *   is nothing approximate left to warn about.
 *
 * @useWhen
 *   - Listing the components of a Shopify native bundle
 *   - The products-list SlidePanel and the bundle detail page (both use it)
 *   - Any surface that shows bundle composition with the same cost wording
 *
 * @dontUseFor
 *   - Spare parts or accessories of a product — use SparePartsSection
 *   - Purchase-order line items — use the PO detail tables
 *   - Bundle CREATION (choosing components) — not a picker; see bundles-erstellen
 *
 * @related
 *   - SparePartsSection — clickable relation rows with a link picker; this list
 *     is read-only and carries quantity and cost
 *   - Pill — the quantity marker ("× 2", accessible name "Quantity 2")
 *   - StatTile — the bundle page shows `describeBundleCost` in one
 *
 * @reuseHints
 *   - A bundle hover card or a future bundle editor preview
 *   - The PO "ordered via bundle" explanation, should Phase 3 surface one
 *
 * @props
 *   components  array    rows from the components payload (`variants[].components`
 *                        or the merged `components`)
 *   loading     boolean  skeleton rows with a polite status, never a spinner
 *   error       string?  message for a failed fetch; renders a `role="alert"`
 *   onRetry     fn?      shows a Retry button in the error state
 *   emptyText   string?  empty-state copy
 */

/** Euro amount, German format. `null`/`undefined`/NaN → the unknown mark. */
export const formatEur = (n) => (
  (n == null || Number.isNaN(Number(n))) ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n))
);

/**
 * True when the parent variants carry DIFFERENT component sets. Mirrors the
 * backend rule (`bundle_components_query.py`): then `parent.rolled_up_cost` is
 * `null` not because no component has a cost, but because one total over the
 * merged list would add both machines of a colour bundle.
 */
export function bundleVariantsDiffer(variants) {
  if (!Array.isArray(variants) || variants.length < 2) return false;
  const keys = new Set(variants.map((v) => (v.components || [])
    .map((c) => String(c.component_variant_id))
    .sort()
    .join('|')));
  return keys.size > 1;
}

/**
 * The ONE wording for a bundle's rolled-up cost, shared by the SlidePanel footer
 * and the bundle page's cost tile.
 *
 * 🔴 **`costSource` decides the wording AND the Σ (A5, 2026-10-09).** It is the
 * provenance the backend computes (`backend/bundle_kosten.py`, owner decision of
 * 2026-10-08): `'components'` — the Σ of the components, the PRIMARY source;
 * `'own'` — the bundle variant's own `cost_per_item`, the FALLBACK, used only
 * because the component sum is incomplete; `'unknown'` — neither is usable.
 *
 * A fallback cost is NOT a sum, so it carries no `Σ` prefix: `Σ 7,25 €` in front
 * of a value that was never added up is a false statement about the number next
 * to it. Before this change the wording said "approximate
 * (representative-variant)" for BOTH sources — a leftover of the display
 * approximation that A1.2 replaced with the automation's own cost term.
 *
 * `rolledUpCost` `null` and `undefined` are the same: unknown. Which unknown it
 * is matters — "no component carries a cost" is false when the parent total is
 * withheld only because the variants differ.
 *
 * 🔴 **`note` is the REASON, `source` is the SOURCE** (R16 W1/N4, R15 W-NEU-1,
 * 2026-10-09). Three surfaces show this, not two: `StatTile`'s hint on the bundle
 * page (prints `note`), the `BundleCostProvenance` line under it (prints
 * `source`), and the SlidePanel footer (prints `note` + the same line since
 * 2026-10-09). Before the split the hint and the line both printed the source —
 * "Sum of the components" stood twice, 40 px apart. One wording per statement:
 * tile and footer say WHY the number is what it is, the line says WHERE.
 *
 * 🔴 **`partial` alone forbids the Σ** (R16 N3 / R15 W1, 2026-10-09). `partial`
 * means "this value is NOT the component sum" — the endpoint sets it exactly
 * when `cost_source != 'components'`. So the conservative test is
 * `partial || costSource === 'own'`: it does not depend on WHICH spelling of
 * "not passed through" a caller uses (`undefined` **or** `null` — `null` is not
 * `undefined`, and `ProductsTable` normalises to `null`), and it cannot be
 * defeated by passing the two fields independently.
 *
 * @returns {{ known: boolean, value: string, note: string, source: string }}
 */
export function describeBundleCost({
  rolledUpCost, partial, components, variantsDiffer, costSource,
}) {
  if (rolledUpCost == null) {
    if (variantsDiffer) {
      return {
        known: false,
        value: 'Varies by variant',
        note: 'Components differ between variants — no single total',
        source: 'unknown',
      };
    }
    return {
      known: false,
      value: 'Unknown',
      note: 'No component carries a cost — nothing to sum',
      source: 'unknown',
    };
  }
  // 🔴 `partial` heisst seit A1.2 „der Wert ist NICHT die Bestandteil-Summe".
  // Ein Σ vor so einer Zahl waere die Behauptung, es sei eine Summe — die
  // konservative Richtung ist darum `own`, unbedingt. Die Prüfung deckt BEIDE
  // Formen ab, mit denen ein Aufrufer „nicht durchgereicht" ausdrücken kann:
  // `undefined` UND `null` (bis 2026-10-09 prüfte sie nur `undefined`, und
  // `ProductsTable` normalisiert auf `null` — der zweite Aufrufer fiel dadurch
  // in den Σ-Zweig, obwohl `partial` galt; R15 W1).
  if (partial || costSource === 'own') {
    return {
      known: true,
      value: formatEur(rolledUpCost),
      note: 'The component sum is incomplete',
      source: 'own',
    };
  }
  return {
    known: true,
    value: `Σ ${formatEur(rolledUpCost)}`,
    note: 'Every component carries a cost',
    source: 'components',
  };
}

function hasCost(c) {
  return c.cost_per_item !== null && c.cost_per_item !== undefined;
}

export default function BundleComponentList({
  components,
  loading = false,
  error = null,
  onRetry,
  emptyText = 'No components recorded for this bundle yet.',
}) {
  if (loading) {
    return (
      <Box role="status">
        <Box component="span" sx={visuallyHidden}>Loading components…</Box>
        {[0, 1, 2].map((i) => (
          <Box key={i} aria-hidden="true" sx={{ py: SP.sm }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="35%" />
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>Retry</Button>
        ) : undefined}
      >
        {error}
      </Alert>
    );
  }

  const rows = components || [];
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
    );
  }

  return (
    <List disablePadding>
      {rows.map((c, i) => {
        const linkable = c.in_catalog && c.sku;
        const qty = c.quantity ?? 1;
        const title = c.title || '(no title)';
        const meta = [
          c.sku || 'No SKU',
          // Ohne Kosten sagt es die Zeile selbst, nicht nur die Summenkachel.
          hasCost(c) ? `Cost ${formatEur(c.cost_per_item * qty)}` : (c.in_catalog ? 'No cost' : null),
          c.in_catalog ? null : 'Not in catalog',
        ].filter(Boolean).join(' · ');
        return (
          <ListItem
            key={`${c.component_variant_id || 'row'}-${i}`}
            divider
            disableGutters
            sx={{ alignItems: 'flex-start', gap: SP.sm, py: SP.sm }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {linkable ? (
                <Link
                  component={RouterLink}
                  to={`/products/${encodeURIComponent(c.sku)}`}
                  variant="body2"
                  underline="hover"
                  sx={{ fontWeight: 600, wordBreak: 'break-word' }}
                >
                  {title}
                </Link>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                  {title}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" component="div">
                {meta}
              </Typography>
            </Box>
            <Stack sx={{ flexShrink: 0 }}>
              <Pill nowrap>
                <Box component="span" aria-hidden="true">× {qty}</Box>
                <Box component="span" sx={visuallyHidden}>Quantity {qty}</Box>
              </Pill>
            </Stack>
          </ListItem>
        );
      })}
    </List>
  );
}
